/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/AddRepoPanel" />
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ExternalConnection } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { getTextWithServiceName, getMaxRepoCount } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { IAddRepoPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/AddRepoPanel.Props";
import { AddRepoPicker } from "Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/AddRepoPicker";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { Panel } from "Wit/Settings/BoardsExternalIntegration/components/panels/Panel.enum";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { GitHubIntegrationCIActions, publishGitHubIntegrationTelemetry } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import { fetchConnectionsData, getFetchGitHubConnectionsErrorText } from "Wit/Settings/BoardsExternalIntegration/utils";

export interface IAddRepoPanelState {
    isDirty: boolean;
    selectedRepos: ExternalGitRepo[];
    reposFromSameProvider?: ExternalGitRepo[];
    errorMessage?: string | JSX.Element;
    loadingSpinnerMessage?: string;
    ignoreNextSelectionChangeEvent?: boolean; // Prevent the selection change event on connection creation failure takes error message away
    hideRepoPicker?: boolean;
}

export class AddRepoPanel extends VssComponent<IAddRepoPanelProps, IAddRepoPanelState, IVssComponentContext> {
    constructor(props: IAddRepoPanelProps, context: IVssComponentContext) {
        super(props, context);

        const { reposFromSameProvider } = this.props;

        this.state = {
            isDirty: false,
            selectedRepos: [],
            reposFromSameProvider: reposFromSameProvider,
            loadingSpinnerMessage: reposFromSameProvider ? undefined : Resources.AddRepo_SpinnerMessage_CheckingExistingConnections
        };

        if (!reposFromSameProvider) {
            this._getReposFromSameProvider();
        }

        publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADD_REPO_PANEL_OPEN);
    }

    public render() {
        const { reposFromSameProvider, errorMessage, loadingSpinnerMessage, hideRepoPicker } = this.state;
        const { connection, isGitHubApp, repositoryType } = this.props;
        // Preselect repo only for new connection. New connection will not service endpoint.
        const shouldPreselectRepo = !!(this.props.connection && !this.props.connection.serviceEndpoint);

        return (
            <BasePanel
                onDismiss={this._onDismiss}
                onDismissErrorMessage={this._onDismissErrorMessage}
                title={
                    this.props.isAcquisitionFlow
                        ? getTextWithServiceName(this.props.repositoryType, Resources.AddRepo_AcquisitionTitle)
                        : getTextWithServiceName(this.props.repositoryType, Resources.AddRepo_Title)
                }
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                errorMessage={errorMessage}
                loadingSpinnerMessage={loadingSpinnerMessage}
            >
                {!hideRepoPicker && (
                    <AddRepoPicker
                        serviceEndpointId={connection.serviceEndpoint && connection.serviceEndpoint.id}
                        isGitHubApp={isGitHubApp}
                        selectedReposFromSameProvider={reposFromSameProvider}
                        onAddReposSelectionChanged={this._onAddReposSelectionChanged}
                        repositoryType={repositoryType}
                        onError={this._onPickerError}
                        shouldPreselectRepo={shouldPreselectRepo}
                    />
                )}
            </BasePanel>
        );
    }

    private async _getReposFromSameProvider(): Promise<void> {
        try {
            const connectionData = await fetchConnectionsData(this.context.pageContext);
            const externalConnections = connectionData.externalConnections || [];
            const reposFromSameProvider: ExternalGitRepo[] = [];
            for (const connection of externalConnections) {
                const { providerKey, externalGitRepos } = connection;
                if (providerKey === this.props.connection.providerKey) {
                    reposFromSameProvider.push(...externalGitRepos);
                }
            }
            this.setState({
                reposFromSameProvider: reposFromSameProvider,
                loadingSpinnerMessage: undefined
            });
        } catch (error) {
            this.setState({ errorMessage: getFetchGitHubConnectionsErrorText(), hideRepoPicker: true, loadingSpinnerMessage: undefined });
        }
    }

    private readonly _onDismissErrorMessage = () => {
        this.setState({ errorMessage: undefined });
    };

    private readonly _renderFooter = (): JSX.Element => {
        return (
            <>
                {!this.props.isGitHubApp && (
                    <span>
                        {Resources.AddRepo_CantFindRepo}{" "}
                        <Link href="#" onClick={this._onAddManuallyClick}>
                            {Resources.AddRepo_AddManually}
                        </Link>
                    </span>
                )}
                <div className="align-right">
                    <Button className="footer-button" text={Resources.Cancel} onClick={this._onDismiss} />
                    <Button
                        className="footer-button"
                        text={Resources.Save}
                        primary={true}
                        disabled={!this._canSave()}
                        onClick={this._onSaveButtonClicked}
                    />
                </div>
            </>
        );
    };

    private readonly _onAddManuallyClick = (): void => {
        this.props.showPanel(Panel.AddRepoManually);
    };

    private readonly _onDismiss = () => {
        // Don't dismiss when busy
        if (!this.state.loadingSpinnerMessage) {
            if (this.state.isDirty) {
                showConfirmationDialog(
                    this.context,
                    Resources.ConfirmationClosePanelMessage,
                    Resources.ConfirmationClosePanelActionText,
                    Resources.ConfirmationClosePanelTitle,
                    this.props.onDismiss
                );
            } else {
                this.props.onDismiss();
            }
        }
    };

    private readonly _onSaveButtonClicked = () => {
        this.setState({ loadingSpinnerMessage: Resources.SpinnerBasePanelSavingText }, this._onSave);
    };

    private readonly _onSave = async (): Promise<void> => {
        const { connection, onSave } = this.props;

        const reposToAdd = [...this.state.selectedRepos];
        const connectionToSave: ExternalConnection = {
            ...connection,
            externalGitRepos: connection.externalGitRepos.concat(reposToAdd)
        };

        let succeeded = true;
        let errorMessage = "";
        let savedConnection: ExternalConnection = connectionToSave;
        try {
            savedConnection = await onSave(connectionToSave);
        } catch (error) {
            succeeded = false;
            errorMessage = error.message;
            this.setState({
                loadingSpinnerMessage: undefined,
                errorMessage: errorMessage,
                ignoreNextSelectionChangeEvent: true // ensure error message is not wiped out by selection change
            });
        } finally {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_ADD_REPOS, {
                addedNumberOfRepos: reposToAdd.length,
                totalNumberOfRepos: savedConnection.externalGitRepos.length,
                manually: false,
                providerKey: savedConnection.providerKey,
                authType: savedConnection.serviceEndpoint.authorizationScheme,
                succeeded: succeeded,
                errorMessage: errorMessage
            });
        }
    };

    private readonly _onAddReposSelectionChanged = (selectedRepos: ExternalGitRepo[]) => {
        this.setState({
            selectedRepos: selectedRepos,
            isDirty: selectedRepos && selectedRepos.length > 0,
            errorMessage: this._isReposCountExceedLimit(selectedRepos)
                ? format(Resources.ExceededMaxReposCountPerConnection, getMaxRepoCount(this.context.pageContext))
                : this.state.ignoreNextSelectionChangeEvent
                ? this.state.errorMessage
                : undefined,
            ignoreNextSelectionChangeEvent: false
        });
    };

    private readonly _onPickerError = (error: Error): void => {
        this.setState({
            loadingSpinnerMessage: undefined,
            errorMessage: error && error.message
        });
    };

    private readonly _renderHeader = (): JSX.Element => {
        const subHeaderText: string = this.props.isAcquisitionFlow
            ? getTextWithServiceName(this.props.repositoryType, Resources.AddRepo_AcquisitionSubHeader)
            : getTextWithServiceName(this.props.repositoryType, Resources.AddRepo_SubHeader);
        return <div>{subHeaderText}</div>;
    };

    private _canSave(): boolean {
        return this.state.isDirty && !this._isReposCountExceedLimit(this.state.selectedRepos);
    }

    private _isReposCountExceedLimit(selectedRepos: ExternalGitRepo[]): boolean {
        const existingRepoCount =
            (this.props.connection && this.props.connection.externalGitRepos && this.props.connection.externalGitRepos.length) || 0;
        const toAddRepoCount = (selectedRepos && selectedRepos.length) || 0;
        const targetRepoCount = existingRepoCount + toAddRepoCount;
        return targetRepoCount > getMaxRepoCount(this.context.pageContext);
    }
}
